/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useState } from "react";
import { SunIcon, MoonIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Accent, getAccent, setAccent } from "@/utils/appearance";
import { getCurrentThemeMode, setAutoTheme, getSystemTheme } from "@/utils/darkMode";
import { cn } from "@/lib/utils";

type ThemeMode = "light" | "dark" | "auto";

const THEMES: { id: ThemeMode; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
  { id: "light", icon: SunIcon,             label: "Light" },
  { id: "dark",  icon: MoonIcon,            label: "Dark"  },
  { id: "auto",  icon: ComputerDesktopIcon, label: "Auto"  },
];

const ACCENTS: { id: Accent; swatch: string; label: string }[] = [
  { id: "blue",     swatch: "#3d71d9", label: "Blue"     },
  { id: "teal",     swatch: "#12a594", label: "Teal"     },
  { id: "amber",    swatch: "#e89c2f", label: "Amber"    },
  { id: "graphite", swatch: "#6b7383", label: "Graphite" },
];

/** Combines theme (light/dark/auto) and accent colour into one button. */
export const StyleToggle: React.FC<{ className?: string; label?: React.ReactNode }> = ({ className, label }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => getCurrentThemeMode());
  const [accent, setAccentState] = useState<Accent>(() => getAccent());

  useEffect(() => {
    const onTheme = () => setThemeState(getCurrentThemeMode());
    const onStyle = () => setAccentState(getAccent());
    window.addEventListener("themechange", onTheme);
    window.addEventListener("storage", onTheme);
    window.addEventListener("accentchange", onStyle);
    return () => {
      window.removeEventListener("themechange", onTheme);
      window.removeEventListener("storage", onTheme);
      window.removeEventListener("accentchange", onStyle);
    };
  }, []);

  const applyTheme = (t: ThemeMode) => {
    if (t === "auto") {
      setAutoTheme();
    } else {
      localStorage.setItem("theme", t);
      document.documentElement.classList.toggle("dark", t === "dark");
    }
    setThemeState(t);
    window.dispatchEvent(new CustomEvent("themechange", {
      detail: { theme: t === "auto" ? getSystemTheme() : t, isSystemChange: false },
    }));
  };

  const CurrentIcon = THEMES.find((t) => t.id === theme)?.icon ?? SunIcon;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={className ?? "text-gray-400 dark:text-gray-500 transition-colors hover:text-gray-700 dark:hover:text-gray-300"}
          aria-label="Style"
          title="Style"
        >
          <CurrentIcon className={className ? "h-[19px] w-[19px] flex-none" : "h-3.5 w-3.5"} />
          {label}
        </button>
      </PopoverTrigger>

      <PopoverContent align={className ? "end" : "start"} side={className ? "right" : "top"} className="w-52 p-1.5 space-y-0.5">
        {/* Theme */}
        <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Theme
        </div>
        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mx-1 mb-1">
          {THEMES.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => applyTheme(id)}
              title={label}
              className={cn(
                "flex-1 flex flex-col items-center gap-1 py-2 text-[10px] font-medium transition-colors",
                theme === id
                  ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white"
                  : "text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        <div className="h-px bg-gray-100 dark:bg-gray-800 mx-1" />

        {/* Accent */}
        <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Accent
        </div>
        <div className="flex items-center gap-2 px-2 pb-1">
          {ACCENTS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => { setAccent(a.id); setAccentState(a.id); }}
              title={a.label}
              aria-label={a.label}
              className={cn(
                "h-5 w-5 rounded-sm ring-offset-1 ring-offset-white dark:ring-offset-gray-900 transition",
                accent === a.id
                  ? "ring-2 ring-gray-400 dark:ring-gray-300"
                  : "ring-1 ring-black/10 dark:ring-white/15 hover:scale-110",
              )}
              style={{ backgroundColor: a.swatch }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
